/**
 * QR code for a catalogue product.
 *
 * The code encodes ONLY the product — never an event — so a printed sticker stays valid
 * forever. Price changes, edits and event configuration never invalidate it. That is the
 * whole point of the standalone channel.
 */
import React from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";

import { TwButton, TwDialog } from "../../components/tailwind";
import { getOnsiteSaleUrl } from "../../utils/productsApi";

/**
 * Generate a QR data-URL for a product, or "" if it can't be produced.
 * Takes the whole product because the URL is supplied by the backend (single source of
 * truth) rather than composed here from an id.
 */
export function useProductQr(product, size = 600) {
  const [dataUrl, setDataUrl] = React.useState("");
  const url = getOnsiteSaleUrl(product);

  React.useEffect(() => {
    let cancelled = false;
    if (!url) {
      setDataUrl("");
      return undefined;
    }

    QRCode.toDataURL(url, { width: size, margin: 1, errorCorrectionLevel: "M" })
      .then((generated) => {
        if (!cancelled) setDataUrl(generated);
      })
      .catch(() => {
        if (!cancelled) setDataUrl("");
      });

    return () => {
      cancelled = true;
    };
  }, [url, size]);

  return { dataUrl, url };
}

const triggerDownload = (dataUrl, name) => {
  if (!dataUrl) return;
  const link = document.createElement("a");
  link.href = dataUrl;
  const safeName = String(name || "product").replace(/\s+/g, "-").toLowerCase();
  link.download = `onsite-qr-${safeName}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Wrap text to a max width, capping at maxLines with an ellipsis. */
const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
    } else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);

  if (lines.length > maxLines) {
    const trimmed = lines.slice(0, maxLines);
    let last = trimmed[maxLines - 1];
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
      last = last.slice(0, -1);
    }
    trimmed[maxLines - 1] = `${last}…`;
    return trimmed;
  }
  return lines;
};

/**
 * Render the QR onto a canvas with the product name (and price) printed underneath.
 *
 * A bare QR is unreadable to a human — with several codes taped around a venue, staff can't
 * tell which sticker is which. The label makes a printed sheet self-identifying.
 *
 * Returns a PNG data URL, or "" if it can't be produced.
 */
/**
 * Render any URL as a QR with a caption beneath it.
 *
 * Extracted from buildLabelledQrPng so the web-shop card can print one code for the whole
 * catalogue using exactly the same layout as the per-product stickers — same size, same
 * padding, same font — so a sheet of printed codes looks consistent whatever it points at.
 */
export async function buildLabelledQrPngFromUrl(url, label) {
  if (!url) return "";

  const QR_SIZE = 600;
  const PADDING = 40;
  const LABEL_AREA = 130; // room for up to two lines of product name

  try {
    const qrDataUrl = await QRCode.toDataURL(url, {
      width: QR_SIZE,
      margin: 1,
      errorCorrectionLevel: "M",
    });

    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = qrDataUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = QR_SIZE + PADDING * 2;
    canvas.height = QR_SIZE + PADDING * 2 + LABEL_AREA;
    const ctx = canvas.getContext("2d");

    // White background — printers and dark-mode viewers both need this explicit.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(image, PADDING, PADDING, QR_SIZE, QR_SIZE);

    const centerX = canvas.width / 2;
    let y = QR_SIZE + PADDING * 2 + 12;

    ctx.textAlign = "center";
    ctx.fillStyle = "#000000";
    ctx.font = "bold 46px Helvetica, Arial, sans-serif";

    // NAME ONLY — deliberately no price. A printed sticker outlives price changes, and a
    // wrong price on a label is far worse than a wrong name: the customer sees it and can
    // reasonably dispute what they were charged. Keeping price off the print means repricing
    // a product never invalidates anything already stuck to a fridge.
    const nameLines = wrapText(ctx, label, canvas.width - PADDING * 2, 2);
    nameLines.forEach((line) => {
      y += 46;
      ctx.fillText(line, centerX, y);
    });

    return canvas.toDataURL("image/png");
  } catch (e) {
    return "";
  }
}

/** Per-product sticker: the product's own sale URL, captioned with its name. */
export async function buildLabelledQrPng(product) {
  return buildLabelledQrPngFromUrl(getOnsiteSaleUrl(product), product?.name);
}

/** Generate a labelled PNG and download it. */
export async function generateAndDownloadQr(product) {
  const dataUrl = await buildLabelledQrPng(product);
  if (!dataUrl) return false;
  triggerDownload(dataUrl, product?.name);
  return true;
}

/** Kept for callers that already hold a raw data URL. */
export function downloadQr(dataUrl, name) {
  triggerDownload(dataUrl, name);
}

export default function ProductQrModal({ open, product = {}, handleClose = () => {} }) {
  const { t } = useTranslation();
  const { dataUrl, url } = useProductQr(product);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard can be blocked; the URL is shown on screen as a fallback.
    }
  };

  return (
    <TwDialog
      dialogOpen={open}
      onClose={handleClose}
      dialogTitle={t("products.qrTitle", "On-site QR code")}
      btnChildren={
        <div className="flex gap-3 justify-end">
          <TwButton variant="primary-outline" onClick={handleClose}>
            {t("common.close", "Close")}
          </TwButton>
          <TwButton disabled={!dataUrl} onClick={() => generateAndDownloadQr(product)}>
            {t("products.downloadPng", "Download PNG")}
          </TwButton>
        </div>
      }
    >
      <div className="flex flex-col items-center text-center gap-3">
        {dataUrl ? (
          <img
            src={dataUrl}
            alt={`QR code for ${product?.name || ""}`}
            className="w-56 h-56"
          />
        ) : (
          <div className="w-56 h-56 bg-gray-100 rounded animate-pulse" />
        )}

        <p className="text-lg font-semibold">{product?.name}</p>
        <p className="text-sm text-gray-500">
          €{parseFloat(product?.onSitePrice || 0).toFixed(2)}
        </p>

        <p className="text-xs text-gray-500 max-w-sm">
          {t(
            "products.qrHelper",
            "Print this and place it next to the item. Guests scan it, choose a quantity and pay on the spot. This code stays valid even if you change the price."
          )}
        </p>

        <button
          type="button"
          onClick={handleCopy}
          className="text-xs text-primary break-all underline"
        >
          {copied ? t("common.copied", "Copied!") : url}
        </button>
      </div>
    </TwDialog>
  );
}
